/**
 * @license Copyright (c) 2003-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
 */

/* globals document, window, LICENSE_KEY, fetch, setInterval, console */

import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import { Bold, Italic, Underline } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CKBox, CKBoxImageEdit } from '@ckeditor/ckeditor5-ckbox';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FontBackgroundColor, FontColor, FontFamily, FontSize } from '@ckeditor/ckeditor5-font';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent';
import {
	PictureEditing,
	Image,
	ImageCaption,
	ImageInsert,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload
} from '@ckeditor/ckeditor5-image';
import { Link } from '@ckeditor/ckeditor5-link';
import { List } from '@ckeditor/ckeditor5-list';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { PageBreak } from '@ckeditor/ckeditor5-page-break';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { SpecialCharacters, SpecialCharactersEssentials } from '@ckeditor/ckeditor5-special-characters';
import { SourceEditingEnhanced } from '@ckeditor/ckeditor5-source-editing-enhanced';
import { Table, TableCellProperties, TableProperties, TableToolbar } from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import { ExportPdf } from '@ckeditor/ckeditor5-export-pdf';
import { ExportWord } from '@ckeditor/ckeditor5-export-word';
import { ImportWord } from '@ckeditor/ckeditor5-import-word';
import { MergeFields } from '@ckeditor/ckeditor5-merge-fields';
import { Mention } from '@ckeditor/ckeditor5-mention';
import { Template } from '@ckeditor/ckeditor5-template';
import { Plugin } from '@ckeditor/ckeditor5-core';

import { TOKEN_URL } from '@ckeditor/ckeditor5-ckbox/tests/_utils/ckbox-config.js';
import { CS_CONFIG } from '@ckeditor/ckeditor5-cloud-services/tests/_utils/cloud-services-config.js';

// Umberto combines all `packages/*/docs` into the `docs/` directory. The import path must be valid after merging all directories.
import ClassicEditor from '../build-classic.js';

ClassicEditor.builtinPlugins.push(
	MergeFields,
	Mention,
	Alignment,
	Essentials,
	Autoformat,
	BlockQuote,
	Bold,
	CKBox,
	CKBoxImageEdit,
	CodeBlock,
	Heading,
	PictureEditing,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	Indent,
	Italic,
	Link,
	List,
	Paragraph,
	Table,
	TableToolbar,
	ExportPdf,
	ExportWord,
	ImportWord,
	FontSize,
	FontFamily,
	FontColor,
	FontBackgroundColor,
	IndentBlock,
	ImageUpload,
	ImageInsert,
	ImageResize,
	MediaEmbed,
	PageBreak,
	PasteFromOffice,
	SpecialCharacters,
	SpecialCharactersEssentials,
	SourceEditingEnhanced,
	TableProperties,
	TableCellProperties,
	TextTransformation,
	Underline,
	CloudServices,
	Template
);

const defaultMergeFieldsConfig = {
	prefix: '{{',
	suffix: '}}',
	previewHtmlValues: true,
	sanitizeHtml: html => ( { html, hasChanged: false } ),
	definitions: [
		{
			groupId: 'guest',
			groupLabel: 'Guest',
			definitions: [
				{
					id: 'guestTitle',
					label: 'Guest title',
					defaultValue: 'Ms./Mr.'
				},
				{
					id: 'guestName',
					label: 'Guest name',
					defaultValue: 'John Doe'
				},
				{
					id: 'guestPhoneNumber',
					label: 'Guest phone number',
					defaultValue: '555-123-456'
				},
				{
					id: 'guestDiscount',
					label: 'Discount rate',
					defaultValue: '10%'
				},
				{
					id: 'additionalValueProposition',
					label: 'SPA recommendations box',
					type: 'block'
				},
				{
					id: 'hotelRoomPhoto',
					label: 'Hotel room photo',
					type: 'image',
					width: 600,
					height: 400
				}
			]
		},
		{
			groupId: 'order',
			groupLabel: 'Order information',
			definitions: [
				{
					id: 'reservationNumber',
					label: 'Reservation number',
					defaultValue: 'VX5493FF'
				},
				{
					id: 'guestArrivalDate',
					label: 'Arrival date',
					defaultValue: () => new Date().toLocaleDateString()
				},
				{
					id: 'guestDepartureDate',
					label: 'Departure date',
					defaultValue: () => new Date().toLocaleDateString()
				},
				{
					id: 'numberOfGuests',
					label: 'Number of guests',
					defaultValue: '1'
				},
				{
					id: 'roomType',
					label: 'Room type',
					defaultValue: 'Single room'
				}
			]
		}
	]
};

ClassicEditor.defaultConfig = {
	mergeFields: defaultMergeFieldsConfig,
	toolbar: {
		items: [
			'|', 'exportPdf', 'exportWord', 'importWord',
			'|', 'sourceEditingEnhanced',
			'|', 'heading',
			'|', 'bold', 'italic', 'underline',
			'|', 'alignment',
			'|', 'bulletedList', 'numberedList',
			'|', 'insertImage'
		]
	},
	menuBar: {
		isVisible: true
	},
	ckbox: {
		tokenUrl: TOKEN_URL,
		allowExternalImagesEditing: [ /^data:/, 'origin', /^([^/]+\.)?cksource\.com\// ],
		forceDemoLabel: true
	},
	fontFamily: {
		supportAllValues: true
	},
	fontSize: {
		options: [ 9, 10, 11, 12, 'default', 14, 15 ],
		supportAllValues: true
	},
	exportPdf: {
		stylesheets: [
			'../assets/spa-fonts.css',
			'EDITOR_STYLES',
			'../assets/spa-export.css'
		],
		fileName: 'export-pdf-demo.pdf',
		appID: 'cke5-docs',
		converterOptions: {
			format: 'Tabloid',
			margin_top: '20mm',
			margin_bottom: '20mm',
			margin_right: '24mm',
			margin_left: '24mm',
			page_orientation: 'portrait'
		},
		tokenUrl: false
	},
	exportWord: {
		stylesheets: [
			'../assets/spa-fonts.css',
			'EDITOR_STYLES',
			'../assets/spa-export.css'
		],
		fileName: 'export-word-demo.docx',
		appID: 'cke5-docs',
		converterOptions: {
			format: 'B4',
			margin_top: '20mm',
			margin_bottom: '20mm',
			margin_right: '12mm',
			margin_left: '12mm',
			page_orientation: 'portrait'
		},
		tokenUrl: false
	},
	importWord: {
		tokenUrl: false,
		defaultStyles: true
	},
	image: {
		toolbar: [
			'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText',
			'|', 'toggleImageCaption', 'imageTextAlternative',
			'|', 'ckboxImageEdit'
		]
	},
	table: {
		contentToolbar: [
			'tableColumn', 'tableRow', 'mergeTableCells',
			'tableProperties', 'tableCellProperties'
		]
	},
	licenseKey: LICENSE_KEY,
	cloudServices: CS_CONFIG,
	ui: {
		viewportOffset: {
			top: window.getViewportTopOffsetConfig()
		}
	}
};

const link = document.createElement( 'link' );
link.rel = 'stylesheet';
link.href = '../assets/spa.css';

document.head.appendChild( link );

const RESOURCE_URL = 'https://api2.binance.com/api/v3/ticker/24hr?symbol=BTCUSDT';

class IntervalFetcher extends Plugin {
	static get pluginName() {
		return 'IntervalFetcher';
	}

	mergeFieldsData = {};

	init() {
		this._updateMergeFieldsData();

		setInterval( () => this._updateMergeFieldsData(), 5000 );
	}

	async _updateMergeFieldsData() {
		try {
			const response = await fetch( RESOURCE_URL );
			const data = await response.json();
			const updateTime = new Date( data.closeTime );
			const parsedData = '$' + Number( data.lastPrice ).toFixed( 2 ) + ' - ' + updateTime.toLocaleString();
			const mergeFieldsData = { bitcoinRate: parsedData };

			this.mergeFieldsData = mergeFieldsData;

			// Refresh the editor so it displays the updated merge fields values.
			this.editor.plugins.get( 'MergeFieldsEditing' ).refreshMergeFields();
		} catch ( error ) {
			console.error( error );
		}
	}
}

window.ClassicEditor = ClassicEditor;
window.CKEditorPlugins = {
	MergeFields,
	IntervalFetcher
};

// CI and Binance do not like each other and the test fails on CI.
const metaElement = document.createElement( 'meta' );

metaElement.name = 'x-cke-crawler-ignore-patterns';
metaElement.content = JSON.stringify( {
	'request-failure': 'binance.com',
	'console-error': [ 'Access to fetch at', 'Failed to fetch' ]
} );

document.head.appendChild( metaElement );
